import { lazy, useEffect } from "react";
import {
  Navigate,
  RouteObject,
  useLocation,
  useParams,
} from "react-router-dom";
import App from "./App";
import { useAuthSession } from "./auth/AuthSessionProvider";
import { AppointmentErrorScope } from "./error-handling/AppointmentErrorScope";
import NotFoundPage from "./pages/NotFoundPage";
import "./pages/Pages.css";
import { ProtectedRoute } from "./pages/components/general/ProtectedRoute";
import UnauthorizedPage from "./pages/components/general/UnauthorizedPage";
import FullPageLayout from "./pages/components/layout/FullPageLayout";
import { SandboxGate } from "./pages/docs/SandboxGate";
// Keep small/critical pages as regular imports
import { INDY_ROUTE_GROUPS } from "./pages/indy/indyAccess";
import LoginPage from "./pages/login/LoginPage";
import { ROUTINE_RUN_ROUTE } from "./pages/routines/run/routineRunPath";
import { ROUTINE_ROUTE_GROUPS } from "./pages/routines/useRoutineAccess";
import { AuthUserGroup } from "./types/auth";

// Lazy load all page components for code splitting
const ActiveCaseListPage = lazy(
  () => import("./pages/active-cases/ActiveCaseListPage"),
);
const AllCasesPage = lazy(() => import("./pages/all-cases/AllCasesPage"));
const CaseDetailsPage = lazy(() => import("./pages/all-cases/CaseDetailsPage"));
const ActiveVisitsPage = lazy(
  () => import("./pages/active-visits/ActiveVisitsPage"),
);
const AppointmentDetailsPage = lazy(
  () => import("./pages/appointments/AppointmentDetailsPage"),
);
const AppointmentListPage = lazy(
  () => import("./pages/appointments/AppointmentListPage"),
);
const NewAppointmentPage = lazy(
  () => import("./pages/appointments/NewAppointmentPage"),
);
const AuditLogsPage = lazy(() => import("./pages/audit-logs"));
const IndyFullscreenPage = lazy(
  () => import("./pages/indy/IndyFullscreenPage"),
);
const RoutineRunPage = lazy(
  () => import("./pages/routines/run/RoutineRunPage"),
);
const TimeTrackingPage = lazy(
  () => import("./pages/time-tracking/TimeTrackingPage"),
);
const FormDraftHistoryPage = lazy(
  () => import("./pages/form-draft-history/FormDraftHistoryPage"),
);
const RecordReviewHistoryPage = lazy(
  () => import("./pages/record-review-history/RecordReviewHistoryPage"),
);
const SplitsPage = lazy(() => import("./pages/splits/SplitsPage"));
const BillingCollectionPage = lazy(
  () => import("./pages/billing-collection/BillingCollectionPage"),
);
const AssessmentPage = lazy(() => import("./pages/documents/AssessmentPage"));
const EvaluationPage = lazy(() => import("./pages/documents/EvaluationPage"));
const HistoryOfInjuryPage = lazy(
  () => import("./pages/documents/HistoryOfInjuryPage"),
);
const MedicalRecordPage = lazy(
  () => import("./pages/documents/MedicalRecordPage"),
);
const ReportPage = lazy(() => import("./pages/documents/ReportPage"));
const ContactsPage = lazy(() => import("./pages/contacts/ContactsPage"));
const OpsSettingsPage = lazy(
  () => import("./pages/ops-settings/OpsSettingsPage"),
);
const MaDetailsPage = lazy(() => import("./pages/ops-settings/MaDetailsPage"));
const NewMaPage = lazy(() => import("./pages/ops-settings/NewMaPage"));
const LibraryPage = lazy(() => import("./pages/library"));
const ReferralPage = lazy(() => import("./pages/referral"));
const SkillsListPage = lazy(() => import("./pages/skills"));
const SkillEditorPage = lazy(() => import("./pages/skills/SkillEditorPage"));
const RoutinesListPage = lazy(() => import("./pages/routines"));
const RoutineEditorPage = lazy(
  () => import("./pages/routines/RoutineEditorPage"),
);
const LocationDetailsPage = lazy(
  () => import("./pages/locations/LocationDetailsPage"),
);
const LocationsListPage = lazy(
  () => import("./pages/locations/LocationsListPage"),
);
const NewLocationPage = lazy(() => import("./pages/locations/NewLocationPage"));
const PastCaseListPage = lazy(
  () => import("./pages/past-cases/PastCaseListPage"),
);
const NewPhysicianPage = lazy(
  () => import("./pages/physicians/NewPhysicianPage"),
);
const PhysicianDetailsPage = lazy(
  () => import("./pages/physicians/PhysicianDetailsPage"),
);
const PhysicianListPage = lazy(
  () => import("./pages/physicians/PhysicianListPage"),
);
const PhysicalEvalTemplateConfiguratorPage = lazy(
  () =>
    import(
      "./pages/physical-eval-template/PhysicalEvalTemplateConfiguratorPage"
    ),
);
const StandardEvalTemplatePage = lazy(
  () => import("./pages/physical-eval-template/StandardEvalTemplatePage"),
);
const PrevisitAppointmentsPage = lazy(
  () => import("./pages/previsit-appointments/PrevisitAppointmentsPage"),
);
const DepositionsPage = lazy(
  () => import("./pages/depositions/DepositionsPage"),
);
const SupplementalsPage = lazy(
  () => import("./pages/supplementals/SupplementalsPage"),
);
const SidebarFullscreenPage = lazy(
  () => import("./pages/sidebar-fullscreen/SidebarFullscreenPage"),
);
const DebugPage = lazy(() => import("./pages/debug/DebugPage"));
const DebugLauncher = lazy(() => import("./pages/debug/DebugLauncher"));
const PromptsBrowser = lazy(() => import("./pages/debug/PromptsBrowser"));
const FeaturesList = lazy(() => import("./pages/debug/features/FeaturesList"));
const FeatureDetail = lazy(
  () => import("./pages/debug/features/FeatureDetail"),
);
const OfflineEvalBrowser = lazy(
  () => import("./pages/debug/OfflineEvalBrowser"),
);
const UploadNewCase = lazy(() => import("./pages/debug/UploadNewCase"));
// Feature-level (whole-pipeline) evals — distinct from OfflineEvalBrowser,
// which manages per-PROMPT eval cases.
const FeatureEvalsPage = lazy(
  () => import("./pages/debug/featureEvals/FeatureEvalsPage"),
);
const FeatureEvalRunDetail = lazy(
  () => import("./pages/debug/featureEvals/RunDetailView"),
);
const FeatureEvalCaseReview = lazy(
  () => import("./pages/debug/featureEvals/EvalCaseReviewRoute"),
);
const FeatureEvalRunCase = lazy(
  () => import("./pages/debug/featureEvals/EvalRunCaseRoute"),
);
const FeatureEvalPoolCase = lazy(
  () => import("./pages/debug/featureEvals/EvalPoolCaseRoute"),
);
const FeatureFlagsDashboard = lazy(
  () => import("./pages/debug/FeatureFlagsDashboard"),
);
const FeatureFlagDetail = lazy(() => import("./pages/debug/FeatureFlagDetail"));
const ZendeskAgentLog = lazy(() => import("./pages/debug/ZendeskAgentLog"));
const AssessmentReviewLog = lazy(
  () => import("./pages/debug/AssessmentReviewLog"),
);
const DictationExtractionLog = lazy(
  () => import("./pages/debug/DictationExtractionLog"),
);
const JobRunLog = lazy(() => import("./pages/debug/JobRunLog"));
const JobRunDetail = lazy(() => import("./pages/debug/JobRunDetail"));
const DictationExtractionDetail = lazy(
  () => import("./pages/debug/DictationExtractionDetail"),
);
// The review surface lives with the Assessment tab it is being integrated into;
// this route is the debug host for it (see AssessmentReviewView's default export).
const AssessmentReviewDebugRoute = lazy(
  () =>
    import(
      "./pages/components/tabs/appointments/AssessmentReviewView/AssessmentReviewView"
    ),
);
const VectorSearchDebug = lazy(() => import("./pages/debug/VectorSearchDebug"));
const IntroductionDebug = lazy(() => import("./pages/debug/IntroductionDebug"));
const HoiGenerationDebug = lazy(
  () => import("./pages/debug/HoiGenerationDebug"),
);
const HistoryTimelineDebug = lazy(
  () => import("./pages/debug/historyTimeline/HistoryTimelineDebug"),
);
const CaseGraphExplorerDebug = lazy(
  () => import("./pages/debug/CaseGraphExplorerDebug"),
);
const ApiPlayground = lazy(() => import("./pages/debug/ApiPlayground"));
const ImpairmentRatingDetail = lazy(
  () => import("./pages/debug/ImpairmentRatingDetail"),
);
const IrFlowDebug = lazy(() => import("./pages/debug/IrFlowDebug"));
const IsItOutDebug = lazy(() => import("./pages/debug/IsItOutDebug"));
const ContactReviewDebug = lazy(
  () => import("./pages/debug/ContactReviewDebug"),
);
const KnowledgeBaseBrowser = lazy(
  () => import("./pages/debug/knowledgeBase/KnowledgeBaseBrowser"),
);
const ExamDictationDebug = lazy(
  () => import("./pages/debug/ExamDictationDebug"),
);
const HistoryLiveDictationDebug = lazy(
  () => import("./pages/debug/HistoryLiveDictationDebug"),
);
const CreateTestAppointment = lazy(
  () => import("./pages/debug/CreateTestAppointment"),
);
const SchemaExplorerHtmlPage = lazy(
  () => import("./pages/debug/schemaExplorerHtml/SchemaExplorerHtmlPage"),
);
const ReportPrototypePage = lazy(
  () => import("./pages/docs/reportPrototype/ReportPrototypePage"),
);
const IndigoDocsListPage = lazy(
  () => import("./pages/docs/IndigoDocsListPage"),
);
const IndigoDocPage = lazy(() => import("./pages/docs/IndigoDocPage"));
const IndigoDocHistoryPage = lazy(
  () => import("./pages/docs/history/IndigoDocHistoryPage"),
);
const RecordReviewRoute = lazy(() =>
  import("./pages/debug/DebugPage").then((m) => ({
    default: m.RecordReviewRoute,
  })),
);
const ComplianceReportListPage = lazy(
  () => import("./pages/compliance-reports/ComplianceReportListPage"),
);
const ComplianceReportDetailPage = lazy(
  () => import("./pages/compliance-reports/ComplianceReportDetailPage"),
);
const ComplianceFlagPage = lazy(
  () => import("./pages/compliance-reports/ComplianceFlagPage"),
);
const TodayVisits = lazy(() => import("./pages/today-visits/TodayVisits"));
const UpcomingAppointmentsPage = lazy(
  () => import("./pages/upcoming-appointments/UpcomingAppointmentsPage"),
);

export const HOME_ROUTES: Record<AuthUserGroup | "DEFAULT", string> = {
  [AuthUserGroup.DOCTOR]: "/today-visits",
  [AuthUserGroup.MEDICAL_ASSISTANT]: "/appointments",
  [AuthUserGroup.HISTORIAN]: "/in-person-evaluations",
  [AuthUserGroup.ADMIN]: "/appointments",
  [AuthUserGroup.UNAUTHORIZED]: "/appointments",
  DEFAULT: "/appointments",
} as const;

const PrivacyRedirect = () => {
  useEffect(() => {
    window.location.replace("https://www.getindigo.io/privacy");
  }, []);
  return null;
};

const ALLOWED_GROUPS_ALL = [
  AuthUserGroup.ADMIN,
  AuthUserGroup.DOCTOR,
  AuthUserGroup.MEDICAL_ASSISTANT,
  AuthUserGroup.HISTORIAN,
];

const ALLOWED_GROUPS_ADMIN_ONLY = [AuthUserGroup.ADMIN];

const ALLOWED_GROUPS_ADMIN_MA = [
  AuthUserGroup.ADMIN,
  AuthUserGroup.MEDICAL_ASSISTANT,
];

const ALLOWED_GROUPS_ADMIN_MA_HISTORIAN = [
  AuthUserGroup.ADMIN,
  AuthUserGroup.MEDICAL_ASSISTANT,
  AuthUserGroup.HISTORIAN,
];

// Every internal role can write skills; AppSync limits non-admins to their
// own skills and keeps publishing admin-only. Doctors get no skills (GET-3797).
const ALLOWED_GROUPS_SKILL_AUTHORS = ALLOWED_GROUPS_ADMIN_MA_HISTORIAN;

const ALLOWED_GROUPS_ADMIN_MA_DOCTOR = [
  AuthUserGroup.ADMIN,
  AuthUserGroup.MEDICAL_ASSISTANT,
  AuthUserGroup.DOCTOR,
];

// /debug/impairment_rating[/:id]?… → /debug/ir_results_insights[/:id]?…
const IrResultsInsightsRedirect = () => {
  const { appointmentRecordId } = useParams<{ appointmentRecordId: string }>();
  const { search } = useLocation();
  const suffix = appointmentRecordId ? `/${appointmentRecordId}` : "";
  return (
    <Navigate to={`/debug/ir_results_insights${suffix}${search}`} replace />
  );
};

// Component for home redirect that uses userGroup from context
const HomeRedirect = () => {
  const { userGroup } = useAuthSession();
  const targetRoute = userGroup ? HOME_ROUTES[userGroup] : HOME_ROUTES.DEFAULT;
  return <Navigate to={targetRoute} replace />;
};

// All routes - single router configuration
export const routes: RouteObject[] = [
  // Public routes (no authentication required)
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/privacy",
    element: <PrivacyRedirect />,
  },

  // Protected routes (authentication required) - all under App wrapper
  {
    path: "/",
    element: <App />,
    children: [
      // Home route
      {
        index: true,
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ALL}>
            <HomeRedirect />
          </ProtectedRoute>
        ),
      },

      // Main navigation routes
      {
        path: "appointments",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_DOCTOR}>
            <AppointmentListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "active-cases",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_DOCTOR}>
            <ActiveCaseListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "past-cases",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_DOCTOR}>
            <PastCaseListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "all-cases",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <AllCasesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "all-cases/:caseId",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <CaseDetailsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "upcoming-appointments",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_DOCTOR}>
            <UpcomingAppointmentsPage />
          </ProtectedRoute>
        ),
      },
      {
        // Alias for MA/Admin - same page, different URL
        path: "bookings",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <UpcomingAppointmentsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "in-person-evaluations",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_HISTORIAN}>
            <PrevisitAppointmentsPage />
          </ProtectedRoute>
        ),
      },
      {
        // Legacy path alias kept for backward compatibility (bookmarks/shared links)
        path: "previsit-appointments",
        element: <Navigate to="/in-person-evaluations" replace />,
      },
      {
        path: "active-visits",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <ActiveVisitsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "depositions",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_DOCTOR}>
            <DepositionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "supplementals",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ALL}>
            <SupplementalsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "billing-and-collection",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <BillingCollectionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "today-visits",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_DOCTOR}>
            <TodayVisits />
          </ProtectedRoute>
        ),
      },
      {
        path: "library",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_DOCTOR}>
            <LibraryPage />
          </ProtectedRoute>
        ),
      },
      {
        // Physician referral dashboard. Nav entry is physician-only; ADMIN is
        // allowed direct-URL access for support/preview.
        path: "referral",
        element: (
          <ProtectedRoute
            allowedGroups={[AuthUserGroup.DOCTOR, AuthUserGroup.ADMIN]}
          >
            <ReferralPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "skills",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_SKILL_AUTHORS}>
            <SkillsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "skills/new",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_SKILL_AUTHORS}>
            <SkillEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "skills/:skillId",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_SKILL_AUTHORS}>
            <SkillEditorPage />
          </ProtectedRoute>
        ),
      },
      // Routines: role here, flags in the page (useRoutineAccess); every
      // /routines/* service call reauthorizes scope and ownership.
      {
        path: "routines",
        element: (
          <ProtectedRoute allowedGroups={ROUTINE_ROUTE_GROUPS}>
            <RoutinesListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "routines/new",
        element: (
          <ProtectedRoute allowedGroups={ROUTINE_ROUTE_GROUPS}>
            <RoutineEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "routines/:routineId",
        element: (
          <ProtectedRoute allowedGroups={ROUTINE_ROUTE_GROUPS}>
            <RoutineEditorPage />
          </ProtectedRoute>
        ),
      },

      // Appointment detail routes
      {
        path: "appointments/:appointmentId",
        element: (
          <AppointmentErrorScope>
            <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ALL}>
              <AppointmentDetailsPage />
            </ProtectedRoute>
          </AppointmentErrorScope>
        ),
      },
      {
        path: "appointments/new/:existingAppointmentId?",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <NewAppointmentPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "appointments/:appointmentId/audit-logs",
        element: (
          <AppointmentErrorScope>
            <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
              <AuditLogsPage />
            </ProtectedRoute>
          </AppointmentErrorScope>
        ),
      },
      {
        path: "appointments/:appointmentId/record-review-history",
        element: (
          <AppointmentErrorScope>
            <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_HISTORIAN}>
              <RecordReviewHistoryPage />
            </ProtectedRoute>
          </AppointmentErrorScope>
        ),
      },
      {
        path: "appointments/:appointmentId/time-tracking",
        element: (
          <AppointmentErrorScope>
            <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
              <TimeTrackingPage />
            </ProtectedRoute>
          </AppointmentErrorScope>
        ),
      },
      {
        path: "appointments/:appointmentId/medical-record-splits",
        element: (
          <AppointmentErrorScope>
            <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_HISTORIAN}>
              <SplitsPage />
            </ProtectedRoute>
          </AppointmentErrorScope>
        ),
      },
      {
        path: "appointments/:appointmentId/form-draft-history",
        element: (
          <AppointmentErrorScope>
            <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_HISTORIAN}>
              <FormDraftHistoryPage />
            </ProtectedRoute>
          </AppointmentErrorScope>
        ),
      },

      // Full-screen Indy chatbot (full page layout — covers the navbar).
      // INDY_ROUTE_GROUPS, not a shared role list: Indy's audience must be
      // widenable without touching unrelated routes. The page applies the
      // flags (useIndyAccess), as MainLayout's entry points do.
      {
        path: "indy",
        element: (
          <FullPageLayout>
            <ProtectedRoute allowedGroups={INDY_ROUTE_GROUPS}>
              <IndyFullscreenPage />
            </ProtectedRoute>
          </FullPageLayout>
        ),
      },
      // A routine run is its conversation, full screen like /indy (GET-4211).
      // The routine service authorizes the run itself.
      {
        path: ROUTINE_RUN_ROUTE.slice(1),
        element: (
          <FullPageLayout>
            <ProtectedRoute allowedGroups={ROUTINE_ROUTE_GROUPS}>
              <RoutineRunPage />
            </ProtectedRoute>
          </FullPageLayout>
        ),
      },

      // Document routes (full page layout)
      {
        path: "medical-records/:medicalRecordId",
        element: (
          <FullPageLayout>
            <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_DOCTOR}>
              <MedicalRecordPage />
            </ProtectedRoute>
          </FullPageLayout>
        ),
      },
      {
        path: "assessment/:assessmentId",
        element: (
          <FullPageLayout>
            <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_DOCTOR}>
              <AssessmentPage />
            </ProtectedRoute>
          </FullPageLayout>
        ),
      },
      {
        path: "evaluation/:evaluationId",
        element: (
          <FullPageLayout>
            <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_DOCTOR}>
              <EvaluationPage />
            </ProtectedRoute>
          </FullPageLayout>
        ),
      },
      {
        path: "history-of-injury/:historyId",
        element: (
          <FullPageLayout>
            <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_DOCTOR}>
              <HistoryOfInjuryPage />
            </ProtectedRoute>
          </FullPageLayout>
        ),
      },
      {
        path: "report-review/:reportId",
        element: (
          <FullPageLayout>
            <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_DOCTOR}>
              <ReportPage />
            </ProtectedRoute>
          </FullPageLayout>
        ),
      },

      // Physician routes
      {
        path: "physicians",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <PhysicianListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "physicians/new",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <NewPhysicianPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "physicians/:physicianId",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <PhysicianDetailsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "physicians/:physicianId/physical-eval-templates",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <PhysicalEvalTemplateConfiguratorPage />
          </ProtectedRoute>
        ),
      },

      // Location routes
      {
        path: "locations",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <LocationsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "locations/new",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <NewLocationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "locations/:locationId",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <LocationDetailsPage />
          </ProtectedRoute>
        ),
      },

      // Schema Explorer — standalone full-screen schema atlas (admin-only, all envs).
      // A dedicated route (not nested under DebugPage) so it renders full-screen.
      {
        path: "debug/schema-explorer-html",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_ONLY}>
            <SchemaExplorerHtmlPage />
          </ProtectedRoute>
        ),
      },

      // Indigo Doc sandbox (specs/indigo-doc/poc.md). Never linked from the
      // nav. Doctors included, unlike /debug: the POC needs doctor logins.
      // Every page below is wrapped in SandboxGate, which checks
      // enable-indigo-doc-sandbox (dev only).
      {
        path: "docs",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ALL}>
            <SandboxGate>
              <IndigoDocsListPage />
            </SandboxGate>
          </ProtectedRoute>
        ),
      },
      {
        path: "docs/report_prototype",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ALL}>
            <SandboxGate>
              <ReportPrototypePage />
            </SandboxGate>
          </ProtectedRoute>
        ),
      },
      {
        // Declared after `docs/report_prototype`, though React Router ranks
        // the static segment above this one either way.
        path: "docs/:docId",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ALL}>
            <SandboxGate>
              <IndigoDocPage />
            </SandboxGate>
          </ProtectedRoute>
        ),
      },
      {
        path: "docs/:docId/history",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ALL}>
            <SandboxGate>
              <IndigoDocHistoryPage />
            </SandboxGate>
          </ProtectedRoute>
        ),
      },

      // Debug route (ADMIN / MA / Historian — no doctors)
      {
        path: "debug",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA_HISTORIAN}>
            <DebugPage />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <DebugLauncher /> },
          { path: "record_review", element: <RecordReviewRoute /> },
          { path: "prompts", element: <PromptsBrowser /> },
          { path: "knowledge_base", element: <KnowledgeBaseBrowser /> },
          { path: "features", element: <FeaturesList /> },
          { path: "features/:key", element: <FeatureDetail /> },
          { path: "offline_evals", element: <OfflineEvalBrowser /> },
          { path: "feature_evals", element: <FeatureEvalsPage /> },
          {
            path: "feature_evals/run/:auditId",
            element: <FeatureEvalRunDetail />,
          },
          {
            // The canonical per-case address: a case is an entry on its run's
            // audit, so (run, entry) identifies it for every eval type.
            path: "feature_evals/run/:auditId/case/:entryId",
            element: <FeatureEvalRunCase />,
          },
          {
            // A pool case outside any run: (eval type, case id), like the API.
            path: "feature_evals/cases/:testCaseId",
            element: <FeatureEvalPoolCase />,
          },
          {
            // Legacy: keyed on the case's ASSESSMENT_REVIEW audit. Kept so
            // links handed out before the route above keep resolving.
            path: "feature_evals/case/:auditId",
            element: <FeatureEvalCaseReview />,
          },
          { path: "upload-new-case", element: <UploadNewCase /> },
          { path: "feature_flags", element: <FeatureFlagsDashboard /> },
          { path: "feature_flags/:key", element: <FeatureFlagDetail /> },
          { path: "zendesk_agent", element: <ZendeskAgentLog /> },
          { path: "assessment_review", element: <AssessmentReviewLog /> },
          {
            path: "dictation_extraction",
            element: <DictationExtractionLog />,
          },
          { path: "job_runs", element: <JobRunLog /> },
          // Audit-keyed: one audit is one run, so the id addresses it directly.
          { path: "job_runs/:auditId", element: <JobRunDetail /> },
          {
            // Appointment-keyed — renders the appointment's LATEST extraction run.
            path: "dictation_extraction/appointment/:appointmentId",
            element: <DictationExtractionDetail />,
          },
          {
            // Appointment-keyed — renders the appointment's LATEST review run.
            path: "assessment_review/appointment/:appointmentId",
            element: <AssessmentReviewDebugRoute />,
          },
          {
            // Same component as the list — the list reads :ticketId from the
            // URL and opens a slide-over drawer with the ticket detail. Direct
            // links to /zendesk_agent/ticket/:ticketId continue to work.
            path: "zendesk_agent/ticket/:ticketId",
            element: <ZendeskAgentLog />,
          },
          { path: "vector_search", element: <VectorSearchDebug /> },
          { path: "introduction", element: <IntroductionDebug /> },
          {
            path: "introduction/appointment/:appointmentId",
            element: <IntroductionDebug />,
          },
          { path: "hoi_generation", element: <HoiGenerationDebug /> },
          { path: "history_timeline", element: <HistoryTimelineDebug /> },
          { path: "case_graph", element: <CaseGraphExplorerDebug /> },
          { path: "api_playground", element: <ApiPlayground /> },
          { path: "ir_results_insights", element: <ImpairmentRatingDetail /> },
          {
            path: "ir_results_insights/:appointmentRecordId",
            element: <ImpairmentRatingDetail />,
          },
          // Old address of the IR results insights page; links in the wild and the
          // sidebar's per-result deep links still work.
          { path: "impairment_rating", element: <IrResultsInsightsRedirect /> },
          {
            path: "impairment_rating/:appointmentRecordId",
            element: <IrResultsInsightsRedirect />,
          },
          { path: "ir_flow", element: <IrFlowDebug /> },
          { path: "is_it_out", element: <IsItOutDebug /> },
          // Read-only ContactReview rows; never writes or enrolls (GET-4405).
          { path: "contact_review", element: <ContactReviewDebug /> },
          {
            path: "contact_review/:appointmentId",
            element: <ContactReviewDebug />,
          },
          { path: "exam_dictation", element: <ExamDictationDebug /> },
          {
            path: "history_live_dictation",
            element: <HistoryLiveDictationDebug />,
          },
          // Dev/stage only — the backing lambda is not deployed to prod, and
          // the launcher hides the tile there.
          {
            path: "create_test_appointment",
            element: <CreateTestAppointment />,
          },
        ],
      },

      // Compliance reports (admin-only, hidden — accessible via direct URL)
      {
        path: "admin/compliance-reports",
        element: (
          <ProtectedRoute allowedGroups={[AuthUserGroup.ADMIN]}>
            <ComplianceReportListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/compliance-reports/:period",
        element: (
          <ProtectedRoute allowedGroups={[AuthUserGroup.ADMIN]}>
            <ComplianceReportDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/compliance-reports/:period/flags/:index",
        element: (
          <ProtectedRoute allowedGroups={[AuthUserGroup.ADMIN]}>
            <ComplianceFlagPage />
          </ProtectedRoute>
        ),
      },

      // Contacts routes
      {
        path: "contacts",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <ContactsPage />
          </ProtectedRoute>
        ),
      },

      // Ops settings routes
      {
        path: "ops-settings",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <OpsSettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "ops-settings/new",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <NewMaPage />
          </ProtectedRoute>
        ),
      },
      {
        // Standard evaluation templates are authored by Admins only (the model
        // also permits MA; see specs/physical-eval-template/template-import.md).
        path: "ops-settings/specialty-categories/:categoryId/evaluation-template",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_ONLY}>
            <StandardEvalTemplatePage />
          </ProtectedRoute>
        ),
      },
      {
        path: "ops-settings/:maId",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ADMIN_MA}>
            <MaDetailsPage />
          </ProtectedRoute>
        ),
      },

      // Utility routes
      {
        path: "sidebar-fullscreen",
        element: (
          <ProtectedRoute allowedGroups={ALLOWED_GROUPS_ALL}>
            <SidebarFullscreenPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "unauthorized",
        element: <UnauthorizedPage />,
      },
      {
        path: "not-found",
        element: <NotFoundPage />,
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
];
